import { AuthorizationType } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"

interface UseIntegrationProps<
    I extends IntegrationType = IntegrationType,
    A extends AuthorizationType = AuthorizationType,
> {
    workspaceId?: string
    userId?: string
    authType?: A
    integration?: I
}

interface UseIntegrationOptions {
    disabled?: boolean
}

const cacheKey = "integration-authorizations"

const invalidateIntegrationAuthorizations = () =>
    mutate((key) => Array.isArray(key) && key[0] === cacheKey)

const useIntegrationAuthorizations = <
    I extends IntegrationType = IntegrationType,
    A extends AuthorizationType = AuthorizationType,
>(
    { workspaceId, userId, authType, integration }: UseIntegrationProps<I, A>,
    { disabled }: UseIntegrationOptions = {}
) => {
    const { data, isLoading, mutate } = useSWR(
        [cacheKey, workspaceId, userId, authType, integration],
        async () => {
            if (!workspaceId) return []

            const query = new URLSearchParams({
                ...(authType ? { authorizationMethod: authType } : {}),
                ...(integration ? { integration } : {}),
            }).toString()

            const authorizations = await fetchApiGateway(
                "get /org/:orgId/integrations/authorizations",
                { params: { orgId: workspaceId }, query }
            )

            return authorizations as IntegrationAuthorizationResponse<A, I>[]
        },
        { isPaused: () => !!disabled }
    )

    return { data, isLoading, invalidateAuthorizationsCache: mutate }
}

interface AuthorizationDelete {
    workspaceId: string
    authorizationId: string
    authUserId: string
}

const useDeleteIntegrationAuthorization = () => {
    const { trigger, isMutating } = useSWRMutation(
        cacheKey,
        async (_key: string, { arg }: { arg: AuthorizationDelete }) =>
            await fetchApiGateway("delete /org/:orgId/integrations/authorizations", {
                params: { orgId: arg.workspaceId },
                query: new URLSearchParams({
                    authorizationId: arg.authorizationId,
                    authUserId: arg.authUserId,
                }).toString(),
            }),
        { onSuccess: () => invalidateIntegrationAuthorizations() }
    )

    return { trigger, isMutating }
}

interface AuthorizationUpdate {
    workspaceId: string
    id: string
    authUserId: string
    fields: { settings: Record<string, unknown> }
}

const useUpdateIntegrationAuthorization = () => {
    const { trigger, isMutating } = useSWRMutation(
        cacheKey,
        async (_key: string, { arg }: { arg: AuthorizationUpdate }) =>
            await fetchApiGateway("patch /org/:orgId/integrations/authorizations", {
                params: { orgId: arg.workspaceId },
                query: new URLSearchParams({
                    authorizationId: arg.id,
                    authUserId: arg.authUserId,
                }).toString(),
                body: arg.fields,
            }),
        { onSuccess: () => invalidateIntegrationAuthorizations() }
    )

    return { trigger, isMutating }
}

export {
    useIntegrationAuthorizations,
    useDeleteIntegrationAuthorization,
    useUpdateIntegrationAuthorization,
    invalidateIntegrationAuthorizations,
}
